import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, MapPin, Phone, Globe, Users, Sparkles, HeartHandshake, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { site, images, signatureDishes, cuisines, experienceFeatures, menuPreview, reviews, galleryItems, whyMango } from "../data/siteData";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { DishCard } from "../components/DishCard";
import { Stars } from "../components/ReviewCard";
import { Marquee } from "../components/Marquee";
import { Lightbox } from "../components/Lightbox";

const ease = [0.22, 1, 0.36, 1];
const iconMap = { Globe, Users, Sparkles, HeartHandshake };

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-[100svh] min-h-[620px] overflow-hidden flex items-center">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <motion.img
          src={images.heroHome}
          alt="Candle-lit premium dining room at Mango World Cuisine Restaurant"
          initial={{ scale: 1.06 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-forest-deep/70 to-transparent" />

      <div className="float-slow absolute top-1/4 right-[12%] h-40 w-40 rounded-full bg-gold/10 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="float-slow absolute bottom-1/4 left-[8%] h-56 w-56 rounded-full bg-forest/30 blur-3xl pointer-events-none" style={{ animationDelay: "2s" }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease }}
          className="text-xs tracking-[0.35em] uppercase font-semibold text-gold"
          data-testid="hero-eyebrow"
        >
          World Cuisine · Bhavnagar
        </motion.p>
        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-ivory tracking-tight leading-[1.02]">
          {["A World of Flavours.", "One Table."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2 -mb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-gold" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.75 + i * 0.18, duration: 1, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8, ease }}
          className="mt-6 max-w-xl text-ivory/85 text-base md:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          From comforting Indian favourites to global flavours, discover food crafted
          for conversations, celebrations and memorable evenings.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.8, ease }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/menu"
            data-testid="hero-explore-menu-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-forest transition-all duration-300 hover:bg-ivory hover:-translate-y-0.5"
          >
            Explore Menu
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={site.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-directions-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-ivory/40 px-8 py-4 text-sm font-semibold text-ivory backdrop-blur-sm transition-all duration-300 hover:border-gold hover:text-gold hover:-translate-y-0.5"
          >
            Get Directions
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 0.9 }}
          className="mt-8 flex items-center gap-3 text-ivory/80"
          data-testid="hero-rating"
        >
          <Stars size={15} />
          <span className="text-sm">4.5 · Loved by 1,400+ diners</span>
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <div>
        <SectionHeading
          overline="Welcome to Mango"
          title="Good food brings people together."
        />
        <Reveal delay={0.15}>
          <p className="mt-6 text-charcoal/70 text-base md:text-lg leading-relaxed max-w-lg">
            Mango – World Cuisine Restaurant brings together flavours from around
            the world in a warm, welcoming space designed for families, friends
            and memorable dining experiences.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <Link
            to="/about"
            data-testid="intro-about-link"
            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-forest"
          >
            Our Story
            <span className="h-px w-8 bg-gold transition-[width] duration-500 group-hover:w-14" />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="relative">
        <div className="overflow-hidden rounded-[2rem]">
          <img
            src={images.intro}
            alt="Warm, welcoming interior of Mango restaurant"
            loading="lazy"
            className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </div>
        <div className="absolute -bottom-6 -left-6 rounded-2xl bg-forest px-7 py-5 text-ivory shadow-xl hidden sm:block">
          <p className="font-display text-3xl text-gold">4.5 ★</p>
          <p className="text-xs text-ivory/70 mt-1 tracking-wide">{site.reviewCount} Google Reviews</p>
        </div>
      </Reveal>
    </div>
  </section>
);

const SignatureDishes = () => (
  <section data-testid="signature-dishes-section" className="py-24 md:py-32 bg-sand/60">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          overline="Signature Dishes"
          title="The Mango Favourites"
          description="The plates our guests return for — generous, beautiful and full of flavour."
        />
        <Reveal delay={0.2}>
          <Link
            to="/menu"
            data-testid="signature-explore-menu-button"
            className="group inline-flex items-center gap-2 rounded-full border border-forest/20 px-6 py-3 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-ivory"
          >
            Explore Full Menu
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 flex gap-6 overflow-x-auto pb-4 no-scrollbar snap-x snap-mandatory md:grid md:grid-cols-3 md:overflow-visible md:pb-0">
        {signatureDishes.map((dish, i) => (
          <Reveal key={dish.name} delay={i * 0.08} className="min-w-[78%] sm:min-w-[46%] md:min-w-0 snap-start">
            <DishCard dish={{ ...dish, category: "Signature" }} compact />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WorldCuisine = () => (
  <section data-testid="world-cuisine-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading
        overline="One Kitchen, Many Worlds"
        title="Flavours Without Borders"
        description="Ten cuisines under one roof — each prepared with the same care."
      />
      <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-line rounded-2xl overflow-hidden border border-line">
        {cuisines.map((c, i) => (
          <motion.div
            key={c}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.05, ease }}
            className="group bg-ivory p-7 md:p-9 transition-colors duration-500 hover:bg-forest"
          >
            <span className="text-[11px] font-semibold tracking-[0.2em] text-charcoal/40 group-hover:text-gold transition-colors duration-500">
              {String(i + 1).padStart(2, "0")}
            </span>
            <p className="mt-4 font-display text-xl md:text-2xl text-charcoal group-hover:text-ivory transition-colors duration-500">
              {c}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Experience = () => (
  <section data-testid="experience-section" className="bg-forest-deep py-24 md:py-32 text-ivory overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <Reveal className="relative order-2 lg:order-1">
        <div className="overflow-hidden rounded-[2rem]">
          <img
            src={images.experience}
            alt="Family and friends sharing a meal at Mango"
            loading="lazy"
            className="aspect-[4/5] lg:aspect-[5/6] w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-ivory/10 pointer-events-none" />
      </Reveal>
      <div className="order-1 lg:order-2">
        <SectionHeading
          dark
          overline="More Than a Meal"
          title="Made for moments worth remembering."
          description="Family dinners, celebrations, casual evenings or simply a craving for something special — Mango creates a comfortable dining experience around good food and warm hospitality."
        />
        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {experienceFeatures.map((f, i) => (
            <Reveal key={f} delay={0.1 + i * 0.07}>
              <li className="flex items-center gap-3 text-ivory/85 text-sm md:text-base">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-gold shrink-0">
                  <Check size={15} />
                </span>
                {f}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const MenuPreviewSection = () => (
  <section data-testid="menu-preview-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading
        overline="From the Kitchen"
        title="A taste of the menu"
        description="A few of the dishes that travel from Burma to Bombay to Bologna — and back."
      />
      <div className="mt-14 border-t border-line">
        {menuPreview.map((item, i) => (
          <Reveal key={item.name} delay={i * 0.04} y={16}>
            <div className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-4 md:gap-8 py-5 md:py-6 border-b border-line transition-colors duration-300 hover:bg-sand/70 px-2 md:px-4 rounded-lg">
              <span className="font-display italic text-sm md:text-base text-gold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display text-xl md:text-2xl text-charcoal transition-transform duration-500 group-hover:translate-x-2">
                  {item.name}
                </h3>
                <p className="mt-1 text-sm text-charcoal/55">{item.description}</p>
              </div>
              <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.2em] text-forest/50 whitespace-nowrap">
                {item.category}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12 text-center">
        <Link
          to="/menu"
          data-testid="menu-preview-view-all-button"
          className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:-translate-y-0.5"
        >
          View Complete Menu
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ReviewsSection = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const current = reviews[idx];

  return (
    <section
      data-testid="reviews-section"
      className="py-24 md:py-32 bg-sand/60"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <SectionHeading align="center" overline="Loved by Bhavnagar" title={`${site.rating} on Google`} />
        <Reveal delay={0.1}>
          <div className="mt-4 flex items-center justify-center gap-3">
            <Stars size={20} />
            <span className="text-sm text-charcoal/60">{site.reviewCount} Google Reviews</span>
          </div>
        </Reveal>
        <div className="relative mt-12 min-h-[220px] md:min-h-[180px]" data-testid="review-carousel">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.55, ease }}
              className="absolute inset-0"
            >
              <p className="font-display text-2xl md:text-3xl leading-snug text-charcoal">
                &ldquo;{current.quote}&rdquo;
              </p>
              <footer className="mt-6 text-sm font-semibold text-forest">
                — {current.name}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            data-testid="review-prev-button"
            aria-label="Previous review"
            onClick={() => setIdx((idx - 1 + reviews.length) % reviews.length)}
            className="p-3 rounded-full border border-forest/15 text-forest hover:bg-forest hover:text-ivory transition-colors duration-300"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {reviews.map((_, i) => (
              <button
                key={i}
                data-testid={`review-dot-${i}`}
                aria-label={`Go to review ${i + 1}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                  i === idx ? "w-8 bg-gold" : "w-1.5 bg-forest/20"
                }`}
              />
            ))}
          </div>
          <button
            data-testid="review-next-button"
            aria-label="Next review"
            onClick={() => setIdx((idx + 1) % reviews.length)}
            className="p-3 rounded-full border border-forest/15 text-forest hover:bg-forest hover:text-ivory transition-colors duration-300"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

const GallerySection = () => {
  const [lightbox, setLightbox] = useState(null);
  const items = galleryItems.slice(0, 8);

  return (
    <section data-testid="gallery-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            overline="Food Gallery"
            title="A Feast for the Eyes"
            description="Sizzlers, fondues, desserts and the spaces in between."
          />
          <Reveal delay={0.2}>
            <Link
              to="/gallery"
              data-testid="gallery-view-all-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-forest"
            >
              View Gallery
              <span className="h-px w-8 bg-gold transition-[width] duration-500 group-hover:w-14" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 columns-2 md:columns-3 lg:columns-4 gap-5 [&>*]:mb-5">
          {items.map((item, i) => (
            <Reveal key={item.src} delay={(i % 4) * 0.07}>
              <button
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setLightbox(i)}
                className="group block w-full overflow-hidden rounded-2xl focus-visible:ring-2"
                aria-label={`Open image: ${item.alt}`}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                    i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/5]"
                  }`}
                />
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
    </section>
  );
};

const WhyMangoSection = () => (
  <section data-testid="why-mango-section" className="py-24 md:py-32 border-t border-line">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading overline="Why Mango" title="Four reasons guests return" />
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {whyMango.map((w, i) => {
          const Icon = iconMap[w.icon];
          return (
            <Reveal key={w.title} delay={i * 0.1}>
              <div className="group" data-testid={`why-mango-${i}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-display italic text-4xl text-gold/80">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon size={26} className="text-forest transition-transform duration-500 group-hover:-translate-y-1" />
                </div>
                <h3 className="mt-5 font-display text-xl md:text-2xl text-charcoal">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{w.description}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const LocationSection = () => (
  <section data-testid="location-section" className="py-24 md:py-32 bg-sand/60">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div>
        <SectionHeading
          overline="Visit Us"
          title="Find Us in the Heart of Bhavnagar"
        />
        <address className="mt-8 not-italic flex flex-col gap-5">
          <p className="flex gap-3 text-charcoal/80 leading-relaxed">
            <MapPin size={18} className="shrink-0 mt-1 text-forest" />
            <span>
              {site.addressLines.slice(0, 3).join(", ")}
              <br />
              {site.addressLines[3]}
            </span>
          </p>
          <p className="flex gap-3 items-center text-charcoal/80">
            <Clock size={18} className="shrink-0 text-forest" />
            <span data-testid="location-hours" className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-600 opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-600" />
              </span>
              {site.hours}
            </span>
          </p>
          <a href={`tel:${site.phoneTel}`} data-testid="location-phone-link" className="flex gap-3 items-center text-charcoal/80 hover:text-forest transition-colors duration-300 w-fit">
            <Phone size={18} className="shrink-0 text-forest" />
            {site.phoneDisplay}
          </a>
        </address>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a
            href={site.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="location-directions-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:-translate-y-0.5"
          >
            Get Directions
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={`tel:${site.phoneTel}`}
            data-testid="location-call-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-ivory"
          >
            <Phone size={15} />
            Call Restaurant
          </a>
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="overflow-hidden rounded-[2rem] border border-line shadow-sm">
          <iframe
            title="Mango – World Cuisine Restaurant on Google Maps"
            data-testid="location-map-embed"
            src={site.mapEmbedUrl}
            className="h-[380px] md:h-[460px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta-section" className="relative overflow-hidden">
    <img
      src={images.finalCta}
      alt="Warm evening ambience at Mango restaurant"
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-forest-deep/70" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight">
          Your table is waiting.
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="mt-5 text-ivory/75 text-base md:text-lg">
          Good food. Great company. A little Mango.
        </p>
      </Reveal>
      <Reveal delay={0.22}>
        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to="/menu"
            data-testid="final-cta-menu-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-forest transition-all duration-300 hover:bg-ivory hover:-translate-y-0.5"
          >
            View Menu
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={site.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-directions-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-ivory/40 px-8 py-4 text-sm font-semibold text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Get Directions
            <ArrowUpRight size={16} />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useEffect(() => {
    document.title = "Mango – World Cuisine Restaurant | Best Restaurant in Bhavnagar";
  }, []);

  return (
    <>
      <Hero />
      <Intro />
      <Marquee />
      <SignatureDishes />
      <WorldCuisine />
      <Experience />
      <MenuPreviewSection />
      <ReviewsSection />
      <GallerySection />
      <WhyMangoSection />
      <LocationSection />
      <FinalCta />
    </>
  );
}
